// Auto-scheduling engine for the from-scratch Schedule Builder
// (pages/Scheduler) — the MS-Project-style forward AND backward pass.
//
// Row shape (editor rows, in visual order):
//   { key, name, indent (0..5), durationDays, manualStart (ISO|""),
//     manualEnd (ISO|"") - SUMMARY ROWS ONLY, see below,
//     predecessors: [{ num, type, lagDays }] (1-based row numbers),
//     milestone }
//
// A SUMMARY'S DATES ARE EDITABLE, WITH THE ROLL-UP AS A FLOOR. Owner,
// 2026-10-01: "parent is changable as long as the children are not taking
// longer than the p[arent, if so, the lowest date a parent can take is the
// last date/finish of his latest child". Confirmed symmetric the same day: a
// parent may not START LATER than its earliest child either.
//
// So for a summary, manualStart and manualEnd EXTEND the span and can never
// shrink it:
//   start = min(rolled-up start, manualStart)
//   end   = max(rolled-up end,   manualEnd)
// A pin inside the children's span is simply inert - the floor wins and the
// row shows the roll-up, which is the honest answer rather than a refusal
// the engine cannot explain. SchedulerPage refuses it AT THE INPUT and names
// the floor date, because that is where a person can be told.
//
// A predecessor may still be given as a bare number; it means the same
// thing it always did — `{ num, type: "FS", lagDays: 0 }`.
//
// computeSchedule(rows, projectStart) returns
//   { rows: [{...row, startDate, endDate, lateStart, lateFinish,
//             totalFloatDays, critical, wbs, isSummary, parentRow}],
//     errors: ["row 4: circular dependency", ...],
//     projectFinish: "yyyy-mm-dd" }
//
// Rules (deliberately MSP-lite):
// - Four relationships, each with a lag in WORKING days (negative = lead):
//     FS  pred finishes -> this starts   (the default)
//     SS  pred starts   -> this starts
//     FF  pred finishes -> this finishes
//     SF  pred starts   -> this finishes
//   A manual start still wins when it is later than what the links give.
// - CALENDAR-aware: which days are working days comes from the row's
//   calendar (5x8, 6x10, 7x24, or one the project made), falling back to
//   the project default and then to Mon-Fri. The third argument is still
//   the company holiday list and still applies on top of EVERY calendar.
//   Durations count that calendar's working days.
// - A relationship between rows on DIFFERENT calendars is resolved on
//   the SUCCESSOR's calendar: the question a link answers is "when can
//   THIS crew start", and it is this crew's week that decides.
// - Milestones have zero duration (start === finish, snapped to a
//   workday).
// - A row followed by a deeper-indent row is a summary: its dates span
//   its children and its own duration/predecessors are ignored
//   (children may still depend on any row).
// - WBS numbers derive from indentation (1, 1.1, 1.2, 2, …).
// - The backward pass runs from the project finish back through the same
//   links; total float is late start minus early start in working days,
//   and a leaf with zero float is on the critical path.
const DAY = 24 * 3600 * 1000;

const iso = (ms) => new Date(ms).toISOString().slice(0, 10);
const parseIso = (s) => {
  const d = new Date(`${String(s).slice(0, 10)}T00:00:00Z`);
  return isNaN(+d) ? null : +d;
};
// A calendar as the engine uses it: the weekdays this crew works, plus
// the dates that are off regardless.
const MON_FRI = [1, 2, 3, 4, 5];
const makeCal = (spec, extraHolidays) => ({
  dows: new Set(
    Array.isArray(spec && spec.workdays) && spec.workdays.length ? spec.workdays : MON_FRI
  ),
  holidays: new Set([...((spec && spec.holidays) || []), ...(extraHolidays || [])]),
  hoursPerDay: Number(spec && spec.hoursPerDay) || 8,
});
// Non-working = a day this crew does not work, or a holiday.
const isNonWorking = (ms, cal) => !cal.dows.has(new Date(ms).getUTCDay()) || cal.holidays.has(iso(ms));
// Snap forward to the next working day.
const toWorkday = (ms, cal) => {
  while (isNonWorking(ms, cal)) ms += DAY;
  return ms;
};
// Snap BACK to the previous working day — the backward pass's version of
// the same idea.
const toWorkdayBack = (ms, cal) => {
  while (isNonWorking(ms, cal)) ms -= DAY;
  return ms;
};
// The next working day STRICTLY AFTER ms, on this calendar. Not
// `addWorkdays(ms, 1)`: that snaps ms forward onto a working day FIRST
// and then adds one, so a predecessor finishing on a day the successor's
// crew does not work (a Friday finish handed to a six-day crew, a
// Saturday finish handed to a five-day one) cost an extra day.
const nextWorkdayAfter = (ms, cal) => toWorkday(ms + DAY, cal);
// The mirror, for the backward pass.
const prevWorkdayBefore = (ms, cal) => toWorkdayBack(ms - DAY, cal);
// Advance n working days from a working day (n=0 → same day).
const addWorkdays = (ms, n, cal) => {
  ms = toWorkday(ms, cal);
  while (n > 0) {
    ms += DAY;
    if (!isNonWorking(ms, cal)) n--;
  }
  return ms;
};
// Retreat n working days.
const subWorkdays = (ms, n, cal) => {
  ms = toWorkdayBack(ms, cal);
  while (n > 0) {
    ms -= DAY;
    if (!isNonWorking(ms, cal)) n--;
  }
  return ms;
};
// Shift by a lag that may be either sign. Zero lag never moves the date,
// including off a non-working day — the caller snaps.
const shiftWorkdays = (ms, lag, cal) => {
  if (!lag) return ms;
  return lag > 0 ? addWorkdays(ms, lag, cal) : subWorkdays(ms, -lag, cal);
};
// Working days from a to b inclusive of a, exclusive of b — used for
// float, so it is a count of days you could slip.
const workdaysBetween = (a, b, cal) => {
  if (a === b) return 0;
  const back = b < a;
  let [from, to] = back ? [b, a] : [a, b];
  let n = 0;
  while (from < to) {
    from += DAY;
    if (!isNonWorking(from, cal)) n++;
  }
  return back ? -n : n;
};

// A predecessor entry, in either the old or the new shape.
const linkOf = (p) => {
  if (p === null || p === undefined) return null;
  if (typeof p === 'number') return { num: p, type: 'FS', lagDays: 0 };
  if (typeof p === 'string') {
    const n = parseInt(p, 10);
    return Number.isFinite(n) ? { num: n, type: 'FS', lagDays: 0 } : null;
  }
  const num = Number(p.num);
  if (!Number.isFinite(num)) return null;
  const type = ['FS', 'SS', 'FF', 'SF'].indexOf(String(p.type || '').toUpperCase()) >= 0
    ? String(p.type).toUpperCase() : 'FS';
  const lagDays = Number.isFinite(Number(p.lagDays)) ? Math.round(Number(p.lagDays)) : 0;
  return { num, type, lagDays };
};

// A row's duration from its saved start and finish, counted the way this
// engine lays the finish out again (finish = start + (duration - 1)
// working days on the row's calendar, company holidays off). The editor
// used to count plain Mon-Fri days on load, so every holiday inside a long
// activity pushed its finish a day later on each save.
export function durationFromDates(startIso, endIso, holidays = [], options = {}, calendarId = null) {
  const s = parseIso(startIso); const e = parseIso(endIso);
  if (s === null || e === null || e < s) return 1;
  const companyHolidays = (holidays || []).map((h) => String((h && h.date) || h).slice(0, 10));
  const id = String(calendarId || (options && options.defaultCalendarId) || '');
  const cal = makeCal(((options && options.calendars) || {})[id], companyHolidays);
  let n = 0;
  for (let t = toWorkday(s, cal); t <= e; t += DAY) if (!isNonWorking(t, cal)) n++;
  return Math.max(1, n);
}

export function computeSchedule(rows, projectStart, holidays = [], options = {}) {
  // holidays: [{date: "yyyy-mm-dd"}] or plain ISO strings - the COMPANY
  // list, which applies on top of every calendar.
  const companyHolidays = (holidays || []).map((h) => String(h?.date || h).slice(0, 10));
  // options.calendars       { id: { workdays, hoursPerDay, holidays } }
  // options.defaultCalendarId  what a row with no calendar of its own uses
  const calSpecs = (options && options.calendars) || {};
  const calCache = new Map();
  // Resolved per row, memoised per calendar id: a 500-row schedule on
  // three calendars builds three.
  const calOf = (row) => {
    const id = String((row && row.calendarId) || (options && options.defaultCalendarId) || '');
    let made = calCache.get(id);
    if (!made) { made = makeCal(calSpecs[id], companyHolidays); calCache.set(id, made); }
    return made;
  };
  const startMs = parseIso(projectStart) || parseIso(new Date().toISOString());
  const n = rows.length;
  const errors = [];

  // Structure: summary flags, parent links, wbs numbering.
  const meta = rows.map((r, i) => ({
    isSummary: i + 1 < n && (rows[i + 1].indent || 0) > (r.indent || 0),
    parentRow: null,
    wbs: "",
  }));
  const counters = [];
  for (let i = 0; i < n; i++) {
    const ind = rows[i].indent || 0;
    counters[ind] = (counters[ind] || 0) + 1;
    counters.length = ind + 1;
    meta[i].wbs = counters.join(".");
    for (let j = i - 1; j >= 0; j--) {
      if ((rows[j].indent || 0) < ind) { meta[i].parentRow = j; break; }
    }
  }

  // Links, validated once, as row indexes.
  const links = rows.map((r, i) => {
    const out = [];
    for (const p of r.predecessors || []) {
      const link = linkOf(p);
      if (!link) continue;
      const pi = link.num - 1;
      if (pi < 0 || pi >= n || pi === i) {
        errors.push(`row ${i + 1}: invalid predecessor "${link.num}"`);
        continue;
      }
      out.push({ ...link, i: pi });
    }
    return out;
  });

  const durOf = (r) => (r.milestone ? 0 : Math.max(0, (Number(r.durationDays) || 1) - 1));

  // ── forward pass ────────────────────────────────────────────────────
  // Leaf rows drive the dates; summaries resolve afterwards from their
  // children. Iterate until stable (bounded — cycles reported).
  const start = new Array(n).fill(null);
  const end = new Array(n).fill(null);
  const leafOrder = rows.map((_, i) => i).filter((i) => !meta[i].isSummary);

  for (let pass = 0; pass <= n; pass++) {
    let changed = false;
    for (const i of leafOrder) {
      const r = rows[i];
      const cal = calOf(r);
      const dur = durOf(r);
      let s = startMs;
      const manual = r.manualStart ? parseIso(r.manualStart) : null;
      if (manual) s = Math.max(s, manual);
      for (const l of links[i]) {
        if (start[l.i] == null || end[l.i] == null) continue;
        // Each type constrains a different pair of ends. FF and SF fix
        // this row's FINISH, so they come back to a start through the
        // duration.
        let want;
        switch (l.type) {
          case 'SS': want = shiftWorkdays(start[l.i], l.lagDays, cal); break;
          case 'FF': want = subWorkdays(shiftWorkdays(end[l.i], l.lagDays, cal), dur, cal); break;
          case 'SF': want = subWorkdays(shiftWorkdays(start[l.i], l.lagDays, cal), dur, cal); break;
          case 'FS':
          default:
            // The next working day after the predecessor finishes, then
            // the lag on top of that.
            want = shiftWorkdays(nextWorkdayAfter(end[l.i], cal), l.lagDays, cal);
            break;
        }
        if (want != null) s = Math.max(s, want);
      }
      s = toWorkday(s, cal);
      const e = addWorkdays(s, dur, cal);
      if (start[i] !== s || end[i] !== e) { start[i] = s; end[i] = e; changed = true; }
    }
    // Summaries span children (deepest-first so nesting resolves).
    //
    // DEEPEST-FIRST IS WHY THE FLOOR IS RECURSIVE FOR FREE: a child that is
    // itself a summary already has its own rolled-up - and possibly pinned -
    // span before its parent reads it here.
    for (let i = n - 1; i >= 0; i--) {
      if (!meta[i].isSummary) continue;
      let s = null, e = null;
      for (let j = i + 1; j < n && (rows[j].indent || 0) > (rows[i].indent || 0); j++) {
        if (start[j] != null) s = s == null ? start[j] : Math.min(s, start[j]);
        if (end[j] != null) e = e == null ? end[j] : Math.max(e, end[j]);
      }
      // A PIN EXTENDS, NEVER SHRINKS. min for the start, max for the finish,
      // so the children's span is the floor in both directions.
      const pinS = rows[i].manualStart ? parseIso(rows[i].manualStart) : null;
      const pinE = rows[i].manualEnd ? parseIso(rows[i].manualEnd) : null;
      if (pinS != null) s = s == null ? pinS : Math.min(s, pinS);
      if (pinE != null) e = e == null ? pinE : Math.max(e, pinE);
      if (s != null && (start[i] !== s || end[i] !== e)) { start[i] = s; end[i] = e; changed = true; }
    }
    if (!changed) break;
    if (pass === n) errors.push("circular dependency detected — dates may be unstable");
  }

  // ── backward pass ───────────────────────────────────────────────────
  // Late finish starts at the project finish and walks back through the
  // successors of each row. A row with no successor can finish as late as
  // the project does; one with successors is pulled back by whichever
  // constrains it most.
  const projectFinish = leafOrder.reduce(
    (m, i) => (end[i] != null && (m == null || end[i] > m) ? end[i] : m),
    null
  );
  const lateStart = new Array(n).fill(null);
  const lateFinish = new Array(n).fill(null);

  if (projectFinish != null) {
    // successors[p] = the links that hang off row p.
    const successors = rows.map(() => []);
    for (let i = 0; i < n; i++) {
      if (meta[i].isSummary) continue;
      for (const l of links[i]) successors[l.i].push({ ...l, of: i });
    }
    for (const i of leafOrder) { lateFinish[i] = projectFinish; lateStart[i] = null; }

    for (let pass = 0; pass <= n; pass++) {
      let changed = false;
      // Walk backwards through the rows so the common case settles in one
      // sweep.
      for (let k = leafOrder.length - 1; k >= 0; k--) {
        const i = leafOrder[k];
        const cal = calOf(rows[i]);
        const dur = durOf(rows[i]);
        let lf = projectFinish;
        for (const s of successors[i]) {
          if (meta[s.of].isSummary) continue;
          const sLS = lateStart[s.of];
          const sLF = lateFinish[s.of];
          if (sLS == null || sLF == null) continue;
          // The mirror of the forward rules: what must THIS row's finish
          // be for its successor's late dates to hold?
          let want;
          switch (s.type) {
            // successor starts when this finishes (+lag)
            case 'FS': want = prevWorkdayBefore(shiftWorkdays(sLS, -s.lagDays, cal), cal); break;
            // successor starts when this starts (+lag) -> constrains this
            // row's START, so push the duration back on to get a finish.
            case 'SS': want = addWorkdays(shiftWorkdays(sLS, -s.lagDays, cal), dur, cal); break;
            // successor finishes when this finishes (+lag)
            case 'FF': want = shiftWorkdays(sLF, -s.lagDays, cal); break;
            // successor finishes when this starts (+lag)
            case 'SF': want = addWorkdays(shiftWorkdays(sLF, -s.lagDays, cal), dur, cal); break;
            default: want = null;
          }
          if (want != null && want < lf) lf = want;
        }
        lf = toWorkdayBack(lf, cal);
        const ls = subWorkdays(lf, dur, cal);
        if (lateFinish[i] !== lf || lateStart[i] !== ls) {
          lateFinish[i] = lf; lateStart[i] = ls; changed = true;
        }
      }
      if (!changed) break;
    }

    // Summaries take the span of their children here too.
    //
    // AND THE PIN HAS TO BE HONOURED HERE AS WELL, which is the half that
    // gets forgotten. Float is late minus early: leave this one rolling up
    // from the children alone and a pinned parent gets an early start from
    // the forward pass and a late start from its children, so its float is
    // the size of the pin - reported as "float is wrong" weeks later by
    // somebody who will never connect it to this feature.
    for (let i = n - 1; i >= 0; i--) {
      if (!meta[i].isSummary) continue;
      let ls = null, lf = null;
      for (let j = i + 1; j < n && (rows[j].indent || 0) > (rows[i].indent || 0); j++) {
        if (lateStart[j] != null) ls = ls == null ? lateStart[j] : Math.min(ls, lateStart[j]);
        if (lateFinish[j] != null) lf = lf == null ? lateFinish[j] : Math.max(lf, lateFinish[j]);
      }
      const pinS = rows[i].manualStart ? parseIso(rows[i].manualStart) : null;
      const pinE = rows[i].manualEnd ? parseIso(rows[i].manualEnd) : null;
      if (pinS != null) ls = ls == null ? pinS : Math.min(ls, pinS);
      if (pinE != null) lf = lf == null ? pinE : Math.max(lf, pinE);
      lateStart[i] = ls; lateFinish[i] = lf;
    }
  }

  return {
    rows: rows.map((r, i) => {
      const float = start[i] != null && lateStart[i] != null
        ? workdaysBetween(start[i], lateStart[i], calOf(r))
        : null;
      return {
        ...r,
        wbs: meta[i].wbs,
        isSummary: meta[i].isSummary,
        parentRow: meta[i].parentRow,
        startDate: start[i] != null ? iso(start[i]) : null,
        endDate: end[i] != null ? iso(end[i]) : null,
        lateStart: lateStart[i] != null ? iso(lateStart[i]) : null,
        lateFinish: lateFinish[i] != null ? iso(lateFinish[i]) : null,
        totalFloatDays: float,
        // Only a leaf can BE critical; a summary just contains critical
        // work, and colouring it red says nothing.
        critical: !meta[i].isSummary && float === 0,
      };
    }),
    errors,
    projectFinish: projectFinish != null ? iso(projectFinish) : null,
  };
}
